// BSP entity lump parsing.

import type { Vec3 } from './math';

export type Entity = Record<string, string> & { classname?: string };

export function parseEntities(text: string): Entity[] {
  const entities: Entity[] = [];
  const re = /"((?:[^"\\]|\\.)*)"|([{}])/g;
  let current: Entity | null = null;
  let key: string | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m[2] === '{') {
      current = {};
      key = null;
    } else if (m[2] === '}') {
      if (current) entities.push(current);
      current = null;
    } else if (current) {
      if (key === null) key = m[1].toLowerCase();
      else {
        current[key] = m[1];
        key = null;
      }
    }
  }
  return entities;
}

export function entityVec(e: Entity, key: string, fallback: Vec3 = [0, 0, 0]): Vec3 {
  const v = e[key];
  if (!v) return [fallback[0], fallback[1], fallback[2]];
  const parts = v.trim().split(/\s+/).map(Number);
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}

export function entityNum(e: Entity, key: string, fallback = 0): number {
  const v = e[key];
  if (v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Yaw from "angle" or "angles" keys, in degrees. */
export function entityAngles(e: Entity): Vec3 {
  if (e.angles) return entityVec(e, 'angles');
  return [0, entityNum(e, 'angle'), 0];
}

/** The "allowteams" key ("axis", "allies") as team bits (1 << team, Axis 0, Allies 1); 0 = every team (G_AllowTeamsAllowed). */
export function allowTeams(e: Entity): number {
  const v = (e.allowteams ?? '').toLowerCase();
  return (v.includes('axis') ? 1 : 0) | (v.includes('allies') ? 2 : 0);
}

/** Inline model index of a brush entity ("*3" -> 3), or -1. */
export function inlineModel(e: Entity): number {
  const m = e.model;
  return m && m.startsWith('*') ? parseInt(m.slice(1), 10) : -1;
}
